import { IncidentSeverity } from './incidents';
import type { NotificationRule, GroupConfig } from './notifications';

/**
 * SLA configuration for a specific severity level
 */
export interface SlaTiming {
  /** Severity level this configuration applies to */
  severity: IncidentSeverity;
  /** Minutes until acknowledgment SLA is breached */
  ackSlaMinutes: number;
  /** Minutes until update SLA is breached */
  updateSlaMinutes: number;
}

/**
 * Sound notification configuration for a severity level
 */
export interface SeveritySoundConfig {
  /** Severity level this configuration applies to */
  severity: IncidentSeverity;
  /** Whether sound notifications are enabled for this severity */
  enabled: boolean;
  /** Base64 encoded audio file data for upload (cleared after save) */
  soundFileData: string | null;
  /** Original filename of uploaded sound */
  soundFileName: string | null;
  /** URL to audio file in blob storage (set by backend after upload) */
  soundFileUrl: string | null;
}

/**
 * Complete settings configuration for the Status Panel
 */
export interface StatusPanelSettings {
  /** SLA timing configuration per severity */
  slaTimings: SlaTiming[];
  /** Sound notification configuration per severity */
  soundConfigs: SeveritySoundConfig[];
  /** Hours of no SignalR messages before showing stale warning (default: 8) */
  staleThresholdHours: number;
  /** Notification rules per severity */
  notificationRules: NotificationRule[];
  /** Configured security groups for notifications */
  groupConfigs: GroupConfig[];
  /** In-app managed list of NIS2 compliance categories available for tagging incidents */
  nis2Categories: string[];
}

/**
 * Default SLA timings matching backend defaults
 */
export const DEFAULT_SLA_TIMINGS: SlaTiming[] = [
  { severity: IncidentSeverity.High, ackSlaMinutes: 15, updateSlaMinutes: 30 },
  { severity: IncidentSeverity.Medium, ackSlaMinutes: 30, updateSlaMinutes: 60 },
  { severity: IncidentSeverity.Low, ackSlaMinutes: 60, updateSlaMinutes: 120 },
  { severity: IncidentSeverity.Informational, ackSlaMinutes: 120, updateSlaMinutes: 240 },
];

/**
 * Default sound configuration (all enabled, no custom sounds)
 */
export const DEFAULT_SOUND_CONFIGS: SeveritySoundConfig[] = [
  { severity: IncidentSeverity.High, enabled: true, soundFileData: null, soundFileName: null, soundFileUrl: null },
  { severity: IncidentSeverity.Medium, enabled: true, soundFileData: null, soundFileName: null, soundFileUrl: null },
  { severity: IncidentSeverity.Low, enabled: false, soundFileData: null, soundFileName: null, soundFileUrl: null },
  { severity: IncidentSeverity.Informational, enabled: false, soundFileData: null, soundFileName: null, soundFileUrl: null },
];

/**
 * Default stale threshold in hours
 */
export const DEFAULT_STALE_THRESHOLD_HOURS = 8;

/**
 * Creates default settings
 */
export const createDefaultSettings = (): StatusPanelSettings => ({
  slaTimings: [...DEFAULT_SLA_TIMINGS],
  soundConfigs: [...DEFAULT_SOUND_CONFIGS],
  staleThresholdHours: DEFAULT_STALE_THRESHOLD_HOURS,
  notificationRules: [],
  groupConfigs: [],
  nis2Categories: [
    'Risk Management',
    'Incident Handling',
    'Business Continuity',
    'Supply Chain Security',
    'Network Security',
    'Access Control',
    'Cryptography',
    'Human Resources Security',
    'Multi-Factor Authentication',
    'Vulnerability Handling',
  ],
});
